import React, { useState, useEffect } from "react";
import {
  Route,
  Switch,
  withRouter,
  BrowserRouter as Router,
  useRouteMatch,
} from "react-router-dom";

import Approve from "./layout/Approve/Container";
import Payment from "./layout/Payment/Container";
import PastEvents from "./layout/PastEvents/Container";
import Review from "./layout/Review/Container";
import Header from "./layout/Header/Header";
import Footer from "./layout/Footer/Footer";
import NotFound from "./components/404/NotFound";
import Nav from "./components/Nav/Nav";
import Login from "./components/SignIn/SignIn";
import Loader from "./components/Loader/Loader";
import ApproveEvent from "./components/ApproveEvent/ApproveEvent";
import PastEvent from "./components/ReviewPastEvent/PastEvent";
import CurrentEvent from "./components/CurrentEvent/CurrentEvent";

import "./index.scss";
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from "@mui/material/styles";
import { UserContext } from "./UserContext";
import { httpPost } from "./hooks/http";
import * as amplitude from "@amplitude/analytics-browser";
import { SnackbarProvider } from "notistack";

const theme = createTheme({
  typography: {
    fontFamily: '"Lato"',
    useNextVariants: true,
  },
});
const snackbarProps = {
  maxSnack: 3,
  autoHideDuration: 1800,
  anchorOrigin: { vertical: "top", horizontal: "center" },
};

const App = (props) => {
  const [clientIdToken, setClientIdToken] = useState(null);
  const [urlToken, setUrlToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eventDetails, setEventDetails] = useState([]);
  const [eventCount, setEventCount] = useState([]);
  const [queryParams, setQueryParams] = useState({});
  const [parseQuery, setParseQuery] = useState(false);

  const addAmp = (result) => {
    if (
      process.env.REACT_APP_AMPLITUDE_KEY &&
      process.env.REACT_APP_AMPLITUDE_KEY.length > 0
    ) {
      window.amplitude = amplitude;
      amplitude.init(
        process.env.REACT_APP_AMPLITUDE_KEY,
        result.response.businessInfo.name + "_tppClientPortal"
      );
      const identity = new amplitude.Identify();
      identity.set("account name", result.response.businessInfo.name);
      identity.set(
        "account tier",
        result.response.businessInfo.subscription.tierName
      );
      identity.set("email", result.response.businessInfo.email);
      identity.set(
        "internal account",
        result.response.businessInfo.isTPPInternalAccount
      );
      identity.set("username", result.response.userInfo.userName);
      amplitude.identify(identity);
      amplitude.setGroup("subBusinessId", result.response.businessInfo.id);
    }
  };

  const idLogin_GetUrl = (clientToken) => {
    return new Promise((resolve, reject) => {
      httpPost(`clientPortal/auth/GetBaseUrl`, { id: clientToken }, false).then(
        (result) => {
          if (!result.loaded) {
            reject();
            setLoading(false);
            return false;
          } else {
            let currentUrl = window.location.toString().toLowerCase();
            let baseUrl = result.response.baseUrl
              ? result.response.baseUrl.toLowerCase()
              : "";
            if (currentUrl.indexOf(baseUrl) !== -1) {
              httpPost(`clientPortal/auth`, { id: clientToken }, false).then(
                (result) => {
                  if (!result.loaded) {
                    reject();
                    setLoading(false);
                    return false;
                  } else {
                    sessionStorage.setItem("authToken", clientToken);
                    // clean up id
                    let uri = window.location.toString();
                    if (uri.indexOf("?") > 0) {
                      let clean_uri = uri.substring(0, uri.indexOf("?"));
                      window.history.replaceState(
                        {},
                        document.title,
                        clean_uri
                      );
                    }
                    resolve(result);
                    // result.response.canViewComments = true;
                  }
                }
              );
            } else {
              var newUrl =
                result.response.baseUrl +
                window.location.pathname +
                window.location.search;
              window.location.href = newUrl;
            }
          }
        }
      );
    });
  };

  const accountLogin = (clientToken, email, name) => {
    return new Promise((resolve, reject) => {
      httpPost(
        `clientPortal/auth/accountLogin`,
        {
          accountId: clientToken,
          emailAddress: email,
          name: name,
        },
        false
      ).then((result) => {
        if (!result.loaded) {
          reject();
          setLoading(false);
          return false;
        }
        resolve(result);
      });
    });
  };

  useEffect(() => {
    setQueryParams(
      window.location.search
        .replace("?", "")
        .split("&")
        .filter(Boolean)
        .map((param) => param.split("="))
        .reduce((values, value) => {
          values[value[0]] = value.slice(1).join("=");
          return values;
        }, {})
    );
    setParseQuery(true);
  }, []);

  useEffect(() => {
    if (!parseQuery) {
      return;
    }
    const paramKeys = Object.keys(queryParams);
    let clientToken = null;
    sessionStorage.removeItem("showWarning");

    if (paramKeys.includes("accountId") || paramKeys.includes("id")) {
      sessionStorage.clear();
    }

    if (paramKeys.includes("id")) {
      sessionStorage.setItem("showOne", true);
    }
    if (paramKeys.includes("accountId")) {
      sessionStorage.removeItem("authToken");
      setClientIdToken(queryParams.accountId);
    } else if (paramKeys.includes("id")) {
      clientToken = decodeURIComponent(queryParams.id);
      sessionStorage.setItem("hasID", true);
    }

    if (!clientToken) clientToken = sessionStorage.getItem("authToken");

    if (clientToken) {
      setUrlToken(clientToken);
      const hasId = sessionStorage.getItem("hasID") === "true";
      var loginPromie = hasId
        ? idLogin_GetUrl(clientToken)
        : accountLogin(
            clientToken,
            sessionStorage.getItem("email"),
            sessionStorage.getItem("lastName")
          );
      loginPromie.then((result) => {
        setEventDetails({
          emailSettings: {
            address: "",
            isAuthenticated: false,
          },
          featureFlags: {
            mentions: false,
          },
          ...result.response,
        });
        setEventCount(result.response.eventCount);
        setLoading(true);
        addAmp(result);
      });
    }
  }, [queryParams, parseQuery]);

  useEffect(() => {
    if (props.location.state) setEventCount(props.location.state.eventCount);
  }, [props]);

  const setToken = (token, result) => {
    setEventDetails(result);
    setUrlToken(token);
    setLoading(true);
  };

  const detectComment = (comment) => {
    if (comment.length > 0) sessionStorage.setItem("showWarning", true);
    else sessionStorage.setItem("showWarning", false);
  };
  const isAcceptPayment = useRouteMatch(`/acceptPayment`);
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <SnackbarProvider {...snackbarProps}>
          <UserContext.Provider value={eventDetails}>
            <Router>
              {urlToken ? (
                loading ? (
                  <React.Fragment>
                    <Header />
                    {Object.keys(eventCount).length > 0 && !isAcceptPayment ? (
                      <Nav />
                    ) : null}

                    <Switch>
                      <Route
                        exact
                        path="/acceptPayment"
                        component={(props) => {
                          return <Payment {...props} isAcceptPayment={true} />;
                        }}
                      />
                      <Route
                        exact
                        path="/current/:id"
                        component={(props) => (
                          <CurrentEvent
                            {...props}
                            detectComment={detectComment}
                          />
                        )}
                      />
                      <Route
                        exact
                        path="/"
                        component={(props) => (
                          <Review {...props} detectComment={detectComment} />
                        )}
                      />
                      <Route path="/login" component={Login} />
                      <Route
                        path="/approve/:id"
                        component={(props) => <ApproveEvent {...props} />}
                      />

                      <Route
                        exact
                        path="/approve"
                        component={() => <Approve />}
                      />

                      <Route path="/payment" component={() => <Payment />} />

                      <Route
                        exact
                        path="/past"
                        component={(props) => <PastEvents {...props} />}
                      />

                      <Route
                        path="/past/:id"
                        component={(props) => <PastEvent {...props} />}
                      />
                      <Route path="/404" component={NotFound} />
                      <Route component={NotFound} />
                    </Switch>
                    <Footer />
                  </React.Fragment>
                ) : (
                  <Loader />
                )
              ) : (
                <Login
                  {...props}
                  clientId={clientIdToken}
                  setToken={setToken}
                />
              )}
            </Router>
          </UserContext.Provider>
        </SnackbarProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
export default withRouter(App);
